export type FilterType = 'none' | 'grayscale' | 'edges';

/**
 * Convert ImageData to grayscale using luminance formula
 * Y = 0.299R + 0.587G + 0.114B
 */
export function toGrayscale(imageData: ImageData): ImageData {
  const { width, height, data } = imageData;
  const output = new Uint8ClampedArray(data.length);

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    // Luminance formula
    const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);

    output[i] = gray;
    output[i + 1] = gray;
    output[i + 2] = gray;
    output[i + 3] = a;
  }

  return new ImageData(output, width, height);
}

/**
 * Apply Sobel edge detection filter
 * Uses Sobel kernels to detect gradients in X and Y directions
 */
export function sobelEdgeDetection(imageData: ImageData): ImageData {
  const { width, height } = imageData;

  // First convert to grayscale
  const gray = toGrayscale(imageData);
  const grayData = gray.data;

  const output = new Uint8ClampedArray(grayData.length);

  // Sobel kernels
  const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
  const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let gx = 0;
      let gy = 0;

      // Apply 3x3 convolution
      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const px = Math.min(Math.max(x + kx, 0), width - 1);
          const py = Math.min(Math.max(y + ky, 0), height - 1);

          const idx = (py * width + px) * 4;
          const pixel = grayData[idx];

          const kernelIdx = (ky + 1) * 3 + (kx + 1);
          gx += pixel * sobelX[kernelIdx];
          gy += pixel * sobelY[kernelIdx];
        }
      }

      // Calculate gradient magnitude
      const magnitude = Math.min(255, Math.sqrt(gx * gx + gy * gy));

      const outIdx = (y * width + x) * 4;
      output[outIdx] = magnitude;
      output[outIdx + 1] = magnitude;
      output[outIdx + 2] = magnitude;
      output[outIdx + 3] = 255;
    }
  }

  return new ImageData(output, width, height);
}

/**
 * Apply the specified filter to ImageData
 */
export function applyFilter(imageData: ImageData, filter: FilterType): ImageData {
  switch (filter) {
    case 'grayscale':
      return toGrayscale(imageData);
    case 'edges':
      return sobelEdgeDetection(imageData);
    case 'none':
    default: {
      // Return a copy, not the original
      const copy = new Uint8ClampedArray(imageData.data);
      return new ImageData(copy, imageData.width, imageData.height);
    }
  }
}
